import { useState } from 'react';
import { Lock, LogIn, ArrowLeft } from 'lucide-react';
import { api, ApiError } from '../api/client';

interface LoginScreenProps {
  onAuthenticated: () => void;
  onBackToSite: () => void;
  brandColor?: string;
}

export default function LoginScreen({
  onAuthenticated,
  onBackToSite,
  brandColor = '#D95D39',
}: LoginScreenProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setPending(true);
    try {
      await api.login(email, password);
      onAuthenticated();
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : 'Connexion impossible. Réessayez dans un instant.'
      );
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFCFB] flex items-center justify-center p-6">
      <div className="w-full max-w-sm space-y-6">
        <button
          onClick={onBackToSite}
          className="text-xs font-bold text-slate-500 hover:text-slate-900 flex items-center gap-1.5 cursor-pointer transition-colors"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          <span>Retour au site</span>
        </button>

        <div className="bg-white border border-slate-200 rounded-2xl shadow-xl p-8 space-y-6">
          <div className="space-y-2 text-center">
            <div
              className="w-12 h-12 rounded-2xl flex items-center justify-center mx-auto text-white"
              style={{ backgroundColor: brandColor }}
            >
              <Lock className="w-6 h-6" />
            </div>
            <h1 className="text-lg font-black tracking-tight text-slate-900 uppercase">
              Espace professionnel
            </h1>
            <p className="text-xs text-slate-500 leading-relaxed">
              Administration, espace client et espace partenaire. Vos identifiants vous ont été
              transmis par l'agence.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1">
              <label htmlFor="login-email" className="block text-[10px] font-black uppercase tracking-wider text-slate-500">
                Adresse e-mail
              </label>
              <input
                id="login-email"
                type="email"
                required
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
              />
            </div>

            <div className="space-y-1">
              <label htmlFor="login-password" className="block text-[10px] font-black uppercase tracking-wider text-slate-500">
                Mot de passe
              </label>
              <input
                id="login-password"
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
              />
            </div>

            {error && (
              <p role="alert" className="text-xs font-bold text-rose-700 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={pending}
              className="w-full py-3 rounded-xl text-white text-xs font-black uppercase tracking-wider flex items-center justify-center gap-2 cursor-pointer disabled:opacity-60 transition-opacity"
              style={{ backgroundColor: brandColor }}
            >
              <LogIn className="w-4 h-4" />
              <span>{pending ? 'Connexion…' : 'Se connecter'}</span>
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
